import React, { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, ShoppingCart, ChefHat, BookOpen, Thermometer, SprayCan, ListChecks,
  ClipboardCheck, Users, Activity, Settings as SettingsIcon, Bell, LogOut, BarChart3, Menu, X, KeyRound,
  CalendarDays, CalendarClock, ListTodo, Clock, Contact, GraduationCap, Award, Wrench, ShieldCheck,
  CalendarCheck, QrCode, Truck, Gift, UserCircle,
} from "lucide-react";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel } from "@/components/ui/dropdown-menu";
import { User as UserIcon } from "lucide-react";
import { Sparkles, Timer, Tablet } from "lucide-react";
import TutorialOverlay from "@/components/TutorialOverlay";
import { DirectoryProvider } from "@/components/TeamIdentity";
import { useApplyBranding } from "@/lib/branding";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, perm: null, group: "Today", module: null },
  { to: "/my-tasks", label: "My Tasks", icon: ListTodo, perm: null, group: "Today", module: null },
  { to: "/temperatures", label: "Temperatures", icon: Thermometer, perm: "view_temperature", group: "Food Safety", module: "temperatures" },
  { to: "/sous-vide", label: "Sous-vide records", icon: Timer, perm: "view_temperature", group: "Food Safety", module: "haccp" },
  { to: "/cleaning", label: "Cleaning", icon: SprayCan, perm: "view_cleaning", group: "Food Safety", module: "cleaning" },
  { to: "/prep", label: "Prep Lists", icon: ListChecks, perm: "view_prep", group: "Food Safety", module: "prep" },
  { to: "/checklists", label: "Checklists", icon: ClipboardCheck, perm: "view_checklists", group: "Food Safety", module: "checklists" },
  { to: "/recipes", label: "Recipes", icon: ChefHat, perm: "view_recipes", group: "Kitchen Management", module: "recipes" },
  { to: "/menus", label: "Menus", icon: BookOpen, perm: "view_menus", group: "Kitchen Management", module: "menus" },
  { to: "/orders", label: "Orders", icon: ShoppingCart, perm: "view_orders", group: "Kitchen Management", module: "orders" },
  { to: "/deliveries", label: "Deliveries", icon: Truck, perm: "view_deliveries", group: "Kitchen Management", module: "deliveries" },
  { to: "/people", label: "People", icon: Contact, perm: "view_people", group: "Workforce", module: null },
  { to: "/workforce", label: "Workforce", icon: CalendarDays, perm: "view_rota", group: "Workforce", module: "rota" },
  { to: "/staff", label: "Staff", icon: Users, perm: "view_staff", group: "Workforce", module: "workforce" },
  { to: "/my-rota", label: "My Rota", icon: CalendarClock, perm: null, group: "Workforce", module: null },
  { to: "/my", label: "My CaterCTRL", icon: UserCircle, perm: null, group: "Workforce", module: null },
  { to: "/perks", label: "Perks", icon: Gift, perm: "perks.redeem", group: "Workforce", module: null },
  { to: "/training", label: "Training", icon: GraduationCap, perm: "view_training", group: "Workforce", module: "training" },
  { to: "/my-training", label: "My Training", icon: Award, perm: null, group: "Workforce", module: "training" },
  { to: "/reporting", label: "Reports", icon: BarChart3, perm: "view_reports", group: "Reports & Compliance", module: "reports" },
  { to: "/assets", label: "Assets", icon: Wrench, perm: "view_assets", group: "Reports & Compliance", module: "assets" },
  { to: "/haccp", label: "HACCP", icon: ShieldCheck, perm: "view_haccp", group: "Reports & Compliance", module: "haccp" },
  { to: "/daily-approval", label: "Daily Approval", icon: CalendarCheck, perm: "view_daily_approval", group: "Reports & Compliance", module: "daily_approval" },
  { to: "/qr-codes", label: "QR Codes", icon: QrCode, perm: "manage_qr", group: "Reports & Compliance", module: "qr" },
  { to: "/allergen-qr", label: "Allergen QR", icon: QrCode, perm: "manage_allergen_qr", group: "Reports & Compliance", module: "allergen_qr" },
  { to: "/activity", label: "Activity", icon: Activity, perm: "view_activity", group: "Reports & Compliance", module: null },
  { to: "/operating", label: "Opening Hours", icon: Clock, perm: "manage_site_operating_status", group: "Admin", module: "operating" },
  { to: "/tablet", label: "Kitchen Tablet", icon: Tablet, perm: "manage_people", group: "Admin", module: null },
  { to: "/settings", label: "Settings", icon: SettingsIcon, perm: "manage_settings", group: "Admin", module: null },
];

const GROUP_ORDER = ["Today", "Food Safety", "Kitchen Management", "Workforce", "Reports & Compliance", "Admin"];

const BOTTOM = ["/dashboard", "/my-tasks", "/cleaning", "/prep", "/checklists"];

export default function Layout({ children }) {
  const { user, logout, has } = useAuth();
  useApplyBranding();
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);
  const [drawer, setDrawer] = useState(false);
  const [sites, setSites] = useState([]);
  const [activeSite, setActiveSite] = useState(localStorage.getItem("cc_site") || "all");
  const [modules, setModules] = useState(null);

  useEffect(() => { api.get("/modules").then((r) => setModules(r.data.modules || {})).catch(() => setModules({})); }, []);

  useEffect(() => { api.get("/me/sites").then((r) => setSites(r.data.sites || [])).catch(() => {}); }, []);
  const changeSite = (v) => { setActiveSite(v); if (v === "all") localStorage.removeItem("cc_site"); else localStorage.setItem("cc_site", v); window.dispatchEvent(new Event("cc-site-changed")); };
  useEffect(() => { const s = () => setActiveSite(localStorage.getItem("cc_site") || "all"); window.addEventListener("cc-site-changed", s); return () => window.removeEventListener("cc-site-changed", s); }, []);

  const visible = NAV.filter((n) => (!n.perm || has(n.perm)) && (!n.module || !modules || modules[n.module] !== false));
  const grouped = GROUP_ORDER.map((g) => ({ g, items: visible.filter((n) => n.group === g) })).filter((x) => x.items.length);
  const bottomNav = visible.filter((n) => BOTTOM.includes(n.to)).slice(0, 5);

  const loadUnread = async () => {
    try {
      const { data } = await api.get("/notifications");
      setUnread(data.filter((n) => !n.read).length);
    } catch {}
  };
  useEffect(() => { loadUnread(); const t = setInterval(loadUnread, 30000); return () => clearInterval(t); }, []);

  const initials = (user?.name || "?").split(" ").map((s) => s[0]).join("").slice(0, 2).toUpperCase();

  const SideLink = ({ n }) => (
    <NavLink
      to={n.to} onClick={() => setDrawer(false)}
      data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
      className={({ isActive }) =>
        `flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-sm font-medium transition-all ${
          isActive ? "bg-brand text-white shadow-sm" : "text-slate-600 hover:bg-slate-100"
        }`}
    >
      <n.icon size={19} strokeWidth={2.1} />
      {n.label}
    </NavLink>
  );

  return (
    <div className="flex min-h-screen bg-slate-50">
      <DirectoryProvider>
      <TutorialOverlay />
      {/* Desktop sidebar */}
      <aside className="hidden md:flex w-64 flex-col bg-white border-r border-slate-200 p-4 fixed top-0 left-0 h-screen z-30">
        <div className="px-1 py-2"><Logo size="md" /></div>
        <nav className="flex flex-col gap-0.5 mt-6 flex-1 min-h-0 overflow-y-auto">
          {grouped.map(({ g, items }) => (
            <div key={g} className="mb-1.5" data-testid={`navgroup-${g.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <div className="px-3 pt-2 pb-1 text-[10px] font-bold uppercase tracking-wider text-slate-400">{g}</div>
              {items.map((n) => <SideLink key={n.to} n={n} />)}
            </div>
          ))}
        </nav>
        <button onClick={() => navigate("/change-password")} data-testid="change-password-link"
          className="flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-sm font-medium text-slate-600 hover:bg-slate-100 transition-all">
          <KeyRound size={19} /> Change password
        </button>
        <button onClick={() => { logout(); navigate("/login"); }} data-testid="logout-button"
          className="flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-sm font-medium text-slate-600 hover:bg-red-50 hover:text-red-600 transition-all">
          <LogOut size={19} /> Sign out
        </button>
      </aside>

      {/* Mobile drawer */}
      {drawer && (
        <div className="md:hidden fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/40" onClick={() => setDrawer(false)} />
          <aside className="relative w-72 bg-white p-4 safe-pt safe-pb flex flex-col animate-in slide-in-from-left">
            <div className="flex items-center justify-between"><Logo /><button onClick={() => setDrawer(false)} data-testid="close-drawer"><X /></button></div>
            <nav className="flex flex-col gap-0.5 mt-6 flex-1 overflow-y-auto">{grouped.map(({ g, items }) => (<div key={g} className="mb-1.5"><div className="px-3 pt-2 pb-1 text-[10px] font-bold uppercase tracking-wider text-slate-400">{g}</div>{items.map((n) => <SideLink key={n.to} n={n} />)}</div>))}</nav>
            <button onClick={() => { setDrawer(false); navigate("/change-password"); }} className="flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-sm font-medium text-slate-600"><KeyRound size={19} /> Change password</button>
            <button onClick={() => { logout(); navigate("/login"); }} className="flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-sm font-medium text-red-600"><LogOut size={19} /> Sign out</button>
          </aside>
        </div>
      )}

      <div className="flex-1 md:ml-64 flex flex-col min-h-screen min-w-0 overflow-x-clip">
        {/* Top bar */}
        <header className="app-topbar sticky top-0 z-20 bg-white/85 backdrop-blur-md border-b border-slate-200 flex items-center justify-between gap-2 min-w-0">
          <div className="flex items-center gap-3">
            <button className="md:hidden" onClick={() => setDrawer(true)} data-testid="open-drawer"><Menu /></button>
            <div className="md:hidden"><Logo compact size="sm" /></div>
          </div>
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            {sites.length > 1 && (
              <select value={activeSite} onChange={(e) => changeSite(e.target.value)} data-testid="site-switcher"
                className="text-sm border border-slate-200 rounded-lg px-2.5 py-1.5 bg-white font-medium text-slate-700 focus:outline-none focus:ring-1 focus:ring-brand min-w-0 max-w-[34vw] sm:max-w-none truncate">
                <option value="all">All sites</option>
                {sites.map((s) => <option key={s.venue_id} value={s.venue_id}>{s.name}</option>)}
              </select>
            )}
            {sites.length === 1 && <span className="text-xs text-slate-500 hidden sm:block px-2 py-1 bg-slate-100 rounded-lg" data-testid="single-site-label">{sites[0].name}</span>}
            <button onClick={() => navigate("/notifications")} data-testid="notifications-bell" className="relative p-2 rounded-full hover:bg-slate-100">
              <Bell size={20} className="text-slate-600" />
              {unread > 0 && <span data-testid="notification-count" className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-[10px] font-bold h-4 min-w-4 px-1 rounded-full flex items-center justify-center">{unread}</span>}
            </button>
            <div className="flex items-center gap-2.5">
              <div className="text-right hidden sm:block">
                <div className="text-sm font-semibold text-slate-800 leading-none">{user?.name}</div>
                <div className="text-[11px] text-slate-400 capitalize">{user?.role}</div>
              </div>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button data-testid="avatar-menu-trigger" className="rounded-full focus:outline-none focus:ring-2 focus:ring-brand" aria-label="Open profile menu">
                    <Avatar className="h-9 w-9 border border-slate-200">
                      <AvatarImage src={user?.avatar} alt={user?.name} />
                      <AvatarFallback className="bg-brand-light text-brand-dark text-xs font-bold">{initials}</AvatarFallback>
                    </Avatar>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-52" data-testid="avatar-menu">
                  <DropdownMenuLabel className="truncate">{user?.name}</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem data-testid="menu-my-profile" onClick={() => navigate("/profile")}><UserIcon size={15} className="mr-2" /> My Profile</DropdownMenuItem>
                  <DropdownMenuItem data-testid="menu-take-tour" onClick={() => window.dispatchEvent(new Event("cc-replay-tutorial"))}><GraduationCap size={15} className="mr-2" /> Take the tour again</DropdownMenuItem>
                  <DropdownMenuItem data-testid="menu-change-password" onClick={() => navigate("/change-password")}><KeyRound size={15} className="mr-2" /> Change password</DropdownMenuItem>
                  {has && has("manage_settings") && <DropdownMenuItem data-testid="menu-branding" onClick={() => navigate("/branding")}><Sparkles size={15} className="mr-2" /> Branding</DropdownMenuItem>}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem data-testid="menu-sign-out" onClick={() => { logout(); navigate("/login"); }} className="text-red-600 focus:text-red-600"><LogOut size={15} className="mr-2" /> Sign out</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        <main className="flex-1 px-4 md:px-8 py-6 pb-24 md:pb-8 max-w-6xl w-full mx-auto fade-in">{children}</main>

        {/* Mobile bottom nav */}
        <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 px-2 py-1.5 safe-pb flex justify-around items-center z-40 shadow-[0_-2px_10px_rgba(0,0,0,0.04)]">
          {bottomNav.map((n) => (
            <NavLink key={n.to} to={n.to} data-testid={`bottomnav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
              className={({ isActive }) => `flex flex-col items-center gap-0.5 px-2 py-1.5 rounded-lg text-[10px] font-medium ${isActive ? "text-brand" : "text-slate-400"}`}>
              <n.icon size={22} strokeWidth={2.1} /> {n.label.split(" ")[0]}
            </NavLink>
          ))}
        </nav>
      </div>
      </DirectoryProvider>
    </div>
  );
}
